import {
  Button,
  Modal,
  Form,
  Select,
  Row,
  Col,
  Tooltip,
} from "antd";
import React, { useEffect, useState } from "react";
import filterIcon from "../../assets/filter.svg";
import filterClearIcon from "../../assets/filter-clear.svg";
import EduLearnerService from "../../services/EduLearnerService";

const { Item } = Form;

interface DropshippingCampFilterProps {
  onChangeFilter: (values: any) => void;
  setFilterOptions: any;
  param: any;
}

const DropshippingCampFilter: React.FC<DropshippingCampFilterProps> = ({
  onChangeFilter,
  setFilterOptions,
  param,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form] = Form.useForm();
  const savedLocalFilters = localStorage.getItem("dropshippingCampFilterText");
  const [crmStatusOptions, setCrmStatusOptions] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const eduStatusOptions = [
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
  ];

  const lastLoginOptions = [
    { value: "true", label: "Has Login" },
    { value: "false", label: "No Login" },
  ];

  const packageFilterOptions = [
    { value: "hasOneamz", label: "Has Oneamz Package" },
    { value: "noOneamz", label: "No Oneamz Package" },
    { value: "hasMonger", label: "Has MongerLab Package" },
    { value: "noMonger", label: "No MongerLab Package" },
  ];

  const showModal = () => {
    setIsModalOpen(true);
    if (crmStatusOptions.length === 0) {
      loadCrmStatuses();
    }
  };

  const loadCrmStatuses = () => {
    setLoading(true);
    EduLearnerService.getCrmStatuses()
      .then((response: any) => {
        const options = response.data.map((item: any) => ({
          value: item.id,
          label: item.name,
        }));
        setCrmStatusOptions(options);
      })
      .catch((e: Error) => {
        console.log("Error loading CRM statuses:", e);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const onFinish = (values: any) => {
    const filters = [];

    if (values.eduStatus !== undefined && values.eduStatus !== null) {
      filters.push({ name: "Status", value: values.eduStatus });
    }

    if (values.crmStatusId !== undefined && values.crmStatusId !== null) {
      const selectedStatus = crmStatusOptions.find(
        (option) => option.value === values.crmStatusId
      );
      filters.push({
        name: "CRM Status",
        value: selectedStatus?.label || values.crmStatusId,
      });
    }

    if (values.hasLastLogin !== undefined && values.hasLastLogin !== null) {
      filters.push({
        name: "Last Login",
        value: values.hasLastLogin === "true" ? "Has Login" : "No Login",
      });
    }

    if (values.packageFilters && values.packageFilters.length > 0) {
      values.packageFilters.forEach((filter: string) => {
        const option = packageFilterOptions.find((opt) => opt.value === filter);
        if (option) {
          filters.push({ name: "App Packages", value: option.label });
        }
      });
    }

    setFilterOptions(filters);
    onChangeFilter(values);
    setIsModalOpen(false);
  };

  const handleOk = () => {
    form.submit();
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  const clearFilter = () => {
    form.resetFields();
    setFilterOptions([]);
    onChangeFilter({});
    setIsModalOpen(false);
  };

  useEffect(() => {
    if (savedLocalFilters) {
      const urlParams = new URLSearchParams(savedLocalFilters);

      const eduStatus = urlParams.get("eduStatus");
      const crmStatusId = urlParams.get("crmStatusId");
      const hasLastLogin = urlParams.get("hasLastLogin");
      const hasOneamzPackage = urlParams.get("hasOneamzPackage");
      const hasMongerPackage = urlParams.get("hasMongerPackage");

      const formValues: any = {};
      const packageFilters: string[] = [];

      if (eduStatus) formValues.eduStatus = eduStatus;
      if (crmStatusId) formValues.crmStatusId = parseInt(crmStatusId);
      if (hasLastLogin) formValues.hasLastLogin = hasLastLogin;
      
      if (hasOneamzPackage === "true") packageFilters.push("hasOneamz");
      if (hasOneamzPackage === "false") packageFilters.push("noOneamz");
      if (hasMongerPackage === "true") packageFilters.push("hasMonger");
      if (hasMongerPackage === "false") packageFilters.push("noMonger");
      
      if (packageFilters.length > 0) {
        formValues.packageFilters = packageFilters;
      }

      form.setFieldsValue(formValues);

      const filters = [];
      if (eduStatus) filters.push({ name: "Status", value: eduStatus });
      if (crmStatusId) filters.push({ name: "CRM Status", value: crmStatusId });
      if (hasLastLogin)
        filters.push({
          name: "Last Login",
          value: hasLastLogin === "true" ? "Has Login" : "No Login",
        });
      
      packageFilters.forEach((filter) => {
        const option = packageFilterOptions.find((opt) => opt.value === filter);
        if (option) {
          filters.push({ name: "App Packages", value: option.label });
        }
      });

      setFilterOptions(filters);
    }
  }, [savedLocalFilters, form, setFilterOptions]);

  return (
    <>
      <Tooltip title="Filter">
        <Button
          type="default"
          size="large"
          onClick={showModal}
          icon={<img src={filterIcon} alt="filter" />}
        />
      </Tooltip>
      <Modal
        title="Filter Dropshipping Camp"
        open={isModalOpen}
        onOk={handleOk}
        onCancel={handleCancel}
        width={800}
        footer={[
          <Button key="clear" onClick={clearFilter}>
            <img src={filterClearIcon} alt="clear" style={{ marginRight: 8 }} />
            Clear Filter
          </Button>,
          <Button key="cancel" onClick={handleCancel}>
            Cancel
          </Button>,
          <Button key="submit" type="primary" onClick={handleOk}>
            Apply Filter
          </Button>,
        ]}
      >
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Row gutter={16}>
            <Col span={12}>
              <Item label="Status" name="eduStatus">
                <Select
                  placeholder="Select status"
                  options={eduStatusOptions}
                  allowClear
                />
              </Item>
            </Col>
            <Col span={12}>
              <Item label="CRM Status" name="crmStatusId">
                <Select
                  placeholder="Select CRM Status"
                  options={crmStatusOptions}
                  showSearch
                  allowClear
                  loading={loading}
                  filterOption={(input, option: any) =>
                    option.label.toLowerCase().includes(input.toLowerCase())
                  }
                />
              </Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Item label="Last Login" name="hasLastLogin">
                <Select
                  placeholder="Select last login status"
                  options={lastLoginOptions}
                  allowClear
                />
              </Item>
            </Col>
            <Col span={12}>
              <Item label="App Packages" name="packageFilters">
                <Select
                  mode="multiple"
                  placeholder="Select package filters"
                  options={packageFilterOptions}
                  allowClear
                  maxTagCount="responsive"
                />
              </Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
};

export default DropshippingCampFilter;
